import { z } from '@customerio/hydra-validation';
import type {
  FacebookLeadAdsCredential as UIAPIFacebookLeadAdsCredential,
  FacebookLeadAdsForm as UIAPIFacebookLeadAdsForm,
} from '@customerio/hydra-ui-api';
import { defineEntity, readSchema } from '../shared/define-entity';

/**
 * Facebook Lead Ads credential + forms (workspace-scoped). The integrations
 * directory reads credential presence (Facebook Lead Ads "enabled") and the
 * count of connected forms (contributes to the Forms card's total).
 */
export type FacebookLeadAdsCredential = UIAPIFacebookLeadAdsCredential;
export type FacebookLeadAdsForm = UIAPIFacebookLeadAdsForm;

const FacebookLeadAdsCredentialFromApi = readSchema<FacebookLeadAdsCredential>(
  z.looseObject({
    page_id: z.string(),
  }),
);

const FacebookLeadAdsFormFromApi = readSchema<FacebookLeadAdsForm>(
  z.looseObject({
    id: z.string(),
    // The handler builds this list from a Go slice it never initialises, so a
    // form with no mapped fields arrives as `null` where the type says array.
    attr_maps: z
      .array(z.looseObject({}))
      .nullable()
      .transform((maps) => maps ?? []),
    // Likewise a Go map it never initialises: a form set up with no extra
    // attributes arrives as `null` where the type says an object.
    extra_attrs: z
      .record(z.string(), z.string())
      .nullish()
      .transform((attrs) => attrs ?? {}),
  }),
);

/** @public */
export const facebookLeadAdsForm = defineEntity<FacebookLeadAdsForm, FacebookLeadAdsForm, void, undefined, 'workspace'>(
  {
    name: 'facebook_lead_ads_form',
    scope: 'workspace',
    pk: (f) => f.id,
    schema: { read: FacebookLeadAdsFormFromApi },
  },
);

export const facebookLeadAdsFormKeys = facebookLeadAdsForm.keys;

/** Credential is a per-workspace singleton — keyed under the form entity's namespace. */
export const facebookLeadAdsCredentialKey = (workspaceId: number) =>
  [...facebookLeadAdsForm.keys.all(workspaceId), 'credential'] as const;

export function parseFacebookLeadAdsCredential(raw: UIAPIFacebookLeadAdsCredential): FacebookLeadAdsCredential {
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  return FacebookLeadAdsCredentialFromApi.parse(raw) as FacebookLeadAdsCredential;
}

export function parseFacebookLeadAdsForm(raw: UIAPIFacebookLeadAdsForm): FacebookLeadAdsForm {
  return facebookLeadAdsForm.parse(raw);
}

/**
 * The statuses a form carries, as the API spells them.
 *
 * `active` is a form taking leads, `inactive` one Facebook has but this
 * workspace is not listening to, and `archived` one archived in Facebook. The
 * API speaks of connecting a form, but no form is ever `connected`: connecting
 * one is setting it `active`.
 */
export const FACEBOOK_FORM_STATUS = {
  active: 'active',
  inactive: 'inactive',
  archived: 'archived',
} as const;

/** A form that is actively connected: its leads reach the workspace. */
export function isConnectedFacebookForm(form: Pick<FacebookLeadAdsForm, 'status'>): boolean {
  return form.status === FACEBOOK_FORM_STATUS.active;
}

/**
 * A form that can be connected now: one Facebook still has that this
 * workspace is not listening to. An archived form is not offered, because
 * Facebook sends it no leads.
 */
export function isConnectableFacebookForm(form: Pick<FacebookLeadAdsForm, 'status'>): boolean {
  return form.status === FACEBOOK_FORM_STATUS.inactive;
}

/**
 * A Facebook form's name without the date the Graph API appends to it.
 *
 * Facebook names a form "Ami Co. form created on Thu Oct 22, 2020 10:04am";
 * the date is noise wherever the form is named. Only the last occurrence is
 * cut, so a form actually named "... created on ..." keeps the part that is
 * its name.
 */
export function facebookFormDisplayName(name: string): string {
  const parts = name.split('created on');
  if (parts.length > 1) {
    parts.pop();
  }
  return parts.join('created on').trim();
}
